import { Box, Skeleton, pn, Typography } from '@compass/core';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { DevExpressReport } from 'components';
import { useIEPData } from '../contexts';
import { IEPFilterControls } from './IEPFilterControls';
import { DashboardCard } from '../components/DashboardCard';
import { IEPDistributionDto } from 'generated/openApi';

const IEPTable = (props: { rows: IEPDistributionDto[]; loading: boolean }) => {
    const appTenantName = useSelector((state: RootState) => state.app.tenantName);
    
    if (props.loading) {
        return <Skeleton variant="rounded" height={400} />;
    }
    
    return (
        <DevExpressReport
            dataSource={props.rows}
            fullPageSize
            showColumnChooser={false}
        >
            <Column
                dataField={pn<IEPDistributionDto>('year')}
                caption="School Year"
                alignment="left"
                cellRender={(data: { value: number }) => `${data.value}-${data.value + 1}`}
            />
            <Column
                dataField={pn<IEPDistributionDto>('iepName')}
                caption="IEP"
                alignment="left"
            />
            <Column
                dataField={pn<IEPDistributionDto>('studentCount')}
                caption="Count"
                alignment="left"
            />
            <Column
                dataField={pn<IEPDistributionDto>('percentage')}
                caption="Percentage"
                alignment="left"
                cellRender={(data: { value: number }) => `${data.value}%`}
            />
            <Summary>
                <TotalItem
                    column={pn<IEPDistributionDto>('year')}
                    customizeText={() => 'Total'}
                />
                <TotalItem
                    column={pn<IEPDistributionDto>('studentCount')}
                    summaryType="sum"
                />
                <TotalItem
                    column={pn<IEPDistributionDto>('percentage')}
                    customizeText={() => '100.0%'}
                />
            </Summary>
        </DevExpressReport>
    );
};

const IEPTableCardContent = () => {
    const { data, loading, error } = useIEPData();

    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <IEPFilterControls />
                <Typography color="error" align="center" sx={{ mt: 4 }}>
                    {error}
                </Typography>
            </Box>
        );
    }
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <IEPFilterControls />
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <IEPTable rows={data} loading={loading} />
            </Box>
        </Box>
    );
};

export const IEPTableCard = () => {
    const { data, filter } = useIEPData();

    // Export handler - passed directly as prop to DashboardCard
    const handleExport = () => {
        if (!data || data.length === 0) return;

        const yearsLabel = filter.schoolYears.length > 0
            ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
            : 'All Years';
        const exportDate = new Date().toLocaleDateString();
        
        // Metadata headers
        const metadata = [
            '# IEP Distribution',
            `# Years: ${yearsLabel}`,
            `# Exported: ${exportDate}`,
            '',
        ];
        
        const headers = 'School Year,IEP,Count,Percentage';
        
        const rows = data.map(item => [
            `${item.year}-${item.year + 1}`,
            `"${item.iepName}"`,
            item.studentCount,
            `${item.percentage}%`
        ]);

        const totalCount = data.reduce((sum, item) => sum + item.studentCount, 0);
        rows.push(['Total', '', totalCount, '100.0%']);

        const csvContent = [
            ...metadata,
            headers, 
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const yearsFilename = filter.schoolYears.length > 0
            ? filter.schoolYears.join('_')
            : 'AllYears';
        link.setAttribute('download', `IEP_${yearsFilename}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <DashboardCard title="IEP" onExport={handleExport}>
            <IEPTableCardContent />
        </DashboardCard>
    );
};